/* eslint-disable react-refresh/only-export-components */
import React from 'react'
import { Sparkles, Check, Building2 } from 'lucide-react'

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl'

/** Display badge derived from profile fields */
export type DisplayBadge = 'premium' | 'expert' | 'company' | null

interface AvatarWithBadgeProps {
  src?: string | null
  fallback: string
  size?: AvatarSize
  /** Pass the resolved display badge (use getBadgeDisplay helper) */
  badge?: DisplayBadge
  className?: string
}

/**
 * Resolves DB badge_type + is_premium into a display badge.
 * - is_premium with no badge_type → 'premium' (diamond)
 * - badge_type 'expert' → 'expert' (blue check)
 * - badge_type 'organization' | 'company' → 'company' (gold check)
 */
export function getBadgeDisplay(
  isPremium: boolean,
  badgeType: string | null | undefined,
): DisplayBadge {
  if (badgeType === 'expert') return 'expert'
  if (badgeType === 'organization' || badgeType === 'company') return 'company'
  if (isPremium) return 'premium'
  return null
}

const SIZES: Record<AvatarSize, string> = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-12 h-12 text-sm',
  lg: 'w-16 h-16 text-lg',
  xl: 'w-24 h-24 text-2xl',
}

const BADGE_ICONS: Record<NonNullable<DisplayBadge>, React.ReactNode> = {
  premium: <Sparkles size={10} />,
  expert: <Check size={10} strokeWidth={3} />,
  company: <Building2 size={10} />,
}

const BADGE_STYLES: Record<NonNullable<DisplayBadge>, string> = {
  premium: 'bg-gradient-to-r from-[#F59E0B] to-[#FCD34D] text-white',
  expert: 'bg-primary text-white',
  company: 'bg-[#F59E0B] text-white',
}

export const AvatarWithBadge: React.FC<AvatarWithBadgeProps> = ({
  src,
  fallback,
  size = 'md',
  badge = null,
  className = '',
}) => {
  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className={`
          rounded-full overflow-hidden flex items-center justify-center font-bold tracking-wider
          bg-gradient-to-br from-primary/80 to-accent/80 text-white
          ${SIZES[size]}
        `}
      >
        {src ? (
          <img src={src} alt="Avatar" className="w-full h-full object-cover" />
        ) : (
          <span>{fallback}</span>
        )}
      </div>

      {badge && (
        <div
          className={`
            absolute -bottom-1 -right-1 flex items-center justify-center rounded-full
            ring-2 ring-[#0A0A0F] text-[0.6rem] w-5 h-5 shadow-sm
            ${BADGE_STYLES[badge]}
          `}
          title={badge}
        >
          {BADGE_ICONS[badge]}
        </div>
      )}
    </div>
  )
}
